import React, { useMemo } from 'react';
import { calculateFinancials } from '../../services/financialCalculations';
import { formatCurrency } from '../../utils/formatters';

const BessAncillaryCapitalCostSensitivityTable = ({ inputs }) => {
    const { sensitivityData, ancillaryRateRange, capitalCostRange } = useMemo(() => {
        const baseAncillaryRate = inputs.bessAncillaryRate;
        const baseCapitalCost = inputs.bessCostPerMWh;

        const ancillaryRateRange = [-0.4, -0.2, 0, 0.2, 0.4].map(mod => baseAncillaryRate * (1 + mod));
        const capitalCostRange = [-0.3, -0.15, 0, 0.15, 0.3].map(mod => baseCapitalCost * (1 + mod));

        const sensitivityData = capitalCostRange.map(cost => {
            return ancillaryRateRange.map(rate => {
                const tempInputs = { ...inputs, bessAncillaryRate: rate, bessCostPerMWh: cost };
                const results = calculateFinancials(tempInputs);
                const bessInvestment = results.investmentPlan?.bessInvestment || 0;
                const annualBessRevenue = results.bessStatement?.[0] ? 
                    (results.bessStatement[0].arbitrageRevenue + results.bessStatement[0].ancillaryRevenue + 
                     results.bessStatement[0].capacityRevenue + results.bessStatement[0].demandChargeReduction - 
                     results.bessStatement[0].operatingCost) : 0;
                return bessInvestment > 0 && annualBessRevenue > 0 ? bessInvestment / annualBessRevenue : Infinity;
            });
        });

        return { sensitivityData, ancillaryRateRange, capitalCostRange };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Ancillary Services vs Capital Cost (Payback Period - Years)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Capital Cost ↓</span>
                                    <span className="font-normal text-xs">Ancillary Rate →</span>
                                </div>
                            </th>
                            {ancillaryRateRange.map((rate, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{formatCurrency(rate, 0)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {formatCurrency(capitalCostRange[rowIndex], 0)}
                                </td>
                                {row.map((payback, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${payback < 15 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {payback === Infinity ? "Never" : payback.toFixed(1)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BessAncillaryCapitalCostSensitivityTable; 